import { computed, ref, type ComputedRef } from 'vue';

import {
  type DocumentRevision,
  removeSelectedRevision,
  upsertSelectedRevisionRecord as upsertRevisionRecord,
} from '@/entities/Document';

import type { DocumentSelectionMode } from './useDocumentSelectionModal';

interface UseDocumentRevisionSelectionOptions {
  selectionMode: DocumentSelectionMode;
  maxSelections?: number;
  alreadySelectedRevisionIds?: number[];
  initialSelectedRevisionId?: number | null;
  initialSelectedRevisionIds?: number[];
  initialSelectedRevisions?: DocumentRevision[];
  selectionLimitMessage: string;
  availableRevisions: ComputedRef<DocumentRevision[]>;
  visibleRevisions: ComputedRef<DocumentRevision[]>;
  syncSelectedRevisionRecords: (
    selectedRevisionIds: number[],
    selectedRevisionRecords: DocumentRevision[]
  ) => DocumentRevision[];
}

export function useDocumentRevisionSelection({
  selectionMode,
  maxSelections,
  alreadySelectedRevisionIds = [],
  initialSelectedRevisionId = null,
  initialSelectedRevisionIds = [],
  initialSelectedRevisions = [],
  selectionLimitMessage,
  availableRevisions,
  visibleRevisions,
  syncSelectedRevisionRecords,
}: UseDocumentRevisionSelectionOptions) {
  const selectedRevisionId = ref<number | null>(initialSelectedRevisionId);
  const selectedRevisionIds = ref<number[]>(resolveInitialSelectedRevisionIds());
  const selectedRevisionRecords = ref<DocumentRevision[]>(initialSelectedRevisions);
  const selectionLimitError = ref('');

  const selectedRevision = computed(
    () =>
      availableRevisions.value.find((revision) => revision.id === selectedRevisionId.value) ?? null
  );

  const selectedRevisions = computed(() =>
    selectedRevisionIds.value
      .map((revisionId) =>
        selectedRevisionRecords.value.find((revision) => revision.id === revisionId)
      )
      .filter((revision): revision is DocumentRevision => revision !== undefined)
  );

  const selectableVisibleRevisions = computed(() =>
    visibleRevisions.value.filter((revision) => !isRevisionAlreadyAdded(revision.id))
  );

  const allVisibleRevisionsSelected = computed(() => {
    const selectable = selectableVisibleRevisions.value;
    if (selectable.length === 0) {
      return false;
    }

    const selectedCount = selectable.filter((revision) => isRevisionSelected(revision.id)).length;

    if (selectedCount === 0) {
      return false;
    }

    return selectedCount === selectable.length || !canAddMore.value;
  });

  const canAddMore = computed(
    () => maxSelections === undefined || selectedRevisionCount.value < maxSelections
  );

  const selectedRevisionCount = computed(() => selectedRevisionIds.value.length);

  /**
   * Whether this row's checkbox can be ticked. An already-ticked one stays tickable, so it can be
   * unticked even at the cap.
   */
  function canSelectRevision(revision: DocumentRevision): boolean {
    return isRevisionSelected(revision.id) || canAddMore.value;
  }

  const selectedRevisionModel = computed({
    get: () => (selectedRevisionId.value === null ? '' : String(selectedRevisionId.value)),
    set: (value: string) => {
      if (value === '') {
        selectedRevisionId.value = null;
        return;
      }

      handleRevisionSelected(Number(value));
    },
  });

  function resolveInitialSelectedRevisionIds(): number[] {
    if (selectionMode === 'multiple') {
      return [...new Set(initialSelectedRevisionIds)];
    }

    if (initialSelectedRevisionId === null) {
      return [];
    }

    return [initialSelectedRevisionId];
  }

  function upsertSelectedRevisionRecord(revision: DocumentRevision): void {
    selectedRevisionRecords.value = upsertRevisionRecord(selectedRevisionRecords.value, revision);
  }

  function syncVisibleSelectedRevisions(): void {
    if (selectionMode !== 'multiple') {
      return;
    }

    selectedRevisionRecords.value = syncSelectedRevisionRecords(
      selectedRevisionIds.value,
      selectedRevisionRecords.value
    );
  }

  function isRevisionSelected(revisionId: number): boolean {
    return selectedRevisionIds.value.includes(revisionId);
  }

  function isRevisionAlreadyAdded(revisionId: number): boolean {
    return alreadySelectedRevisionIds.includes(revisionId);
  }

  function isRevisionActive(revisionId: number): boolean {
    return selectionMode === 'multiple'
      ? isRevisionSelected(revisionId)
      : selectedRevisionId.value === revisionId;
  }

  function clearSelectionLimitError(): void {
    selectionLimitError.value = '';
  }

  function toggleRevision(revision: DocumentRevision): void {
    if (isRevisionAlreadyAdded(revision.id)) {
      return;
    }

    if (isRevisionSelected(revision.id)) {
      const nextSelection = removeSelectedRevision(
        selectedRevisionIds.value,
        selectedRevisionRecords.value,
        revision.id
      );

      selectedRevisionIds.value = nextSelection.selectedRevisionIds;
      selectedRevisionRecords.value = nextSelection.selectedRevisionRecords;
      selectionLimitError.value = '';
      return;
    }

    if (!canAddMore.value) {
      selectionLimitError.value = selectionLimitMessage;
      return;
    }

    selectedRevisionIds.value = [...selectedRevisionIds.value, revision.id];
    upsertSelectedRevisionRecord(revision);
    selectionLimitError.value = '';
  }

  function handleRevisionSelected(revisionId: number): void {
    if (selectionMode === 'multiple') {
      const revision = availableRevisions.value.find((item) => item.id === revisionId);
      if (revision) {
        toggleRevision(revision);
      }

      return;
    }

    selectedRevisionId.value = revisionId;
  }

  function toggleAllVisibleRevisions(): void {
    const targets = selectableVisibleRevisions.value;

    if (allVisibleRevisionsSelected.value) {
      const removedIds = new Set(targets.map((revision) => revision.id));

      selectedRevisionIds.value = selectedRevisionIds.value.filter(
        (revisionId) => !removedIds.has(revisionId)
      );
      selectedRevisionRecords.value = selectedRevisionRecords.value.filter(
        (revision) => !removedIds.has(revision.id)
      );
      selectionLimitError.value = '';
      return;
    }

    const unselected = targets.filter((revision) => !isRevisionSelected(revision.id));

    let revisionsToAdd = unselected;
    if (maxSelections !== undefined) {
      const remaining = maxSelections - selectedRevisionCount.value;
      revisionsToAdd = unselected.slice(0, Math.max(0, remaining));
    }

    selectedRevisionIds.value = [
      ...selectedRevisionIds.value,
      ...revisionsToAdd.map((revision) => revision.id),
    ];
    revisionsToAdd.forEach(upsertSelectedRevisionRecord);

    selectionLimitError.value = '';
  }

  return {
    allVisibleRevisionsSelected,
    canAddMore,
    canSelectRevision,
    clearSelectionLimitError,
    handleRevisionSelected,
    isRevisionActive,
    isRevisionAlreadyAdded,
    isRevisionSelected,
    selectedRevision,
    selectedRevisionCount,
    selectedRevisionId,
    selectedRevisionIds,
    selectedRevisionModel,
    selectedRevisionRecords,
    selectedRevisions,
    selectionLimitError,
    syncVisibleSelectedRevisions,
    toggleAllVisibleRevisions,
    toggleRevision,
  };
}
